// =============================================================================
// GOALS SERVICE - Firebase CRUD for Goals
// ⚓ ANCHORED: 2026-01-27 - Kritieke Firestore data sanitatie.
// NIET WIJZIGEN ZONDER OVERLEG. Zie PROJECT_NOTES/DOELSTELLINGEN_FIXES_2026-01-27.md
// =============================================================================

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { Goal, GoalType, GoalQuestionnaireAnswers, GoalCommitment, GoalBalanceCategory, GoalMainCategory } from '../types';

const COLLECTION_NAME = 'goals';

// ⚓ ANCHORED: 2026-01-27 - Helper om undefined velden te verwijderen.
// Firestore accepteert geen undefined values - dit voorkomt write errors.
function removeUndefinedFields<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined) {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          // Recursief voor nested objects (zoals commitment)
          result[key] = removeUndefinedFields(value as Record<string, unknown>) as T[typeof key];
        } else {
          result[key] = value;
        }
      }
    }
  }
  return result;
}

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

/**
 * Create a new goal in Firestore
 * @param ownerId - Firebase Auth UID
 * @param data - Goal data (without id, createdAt, ownerId)
 * @returns The created goal's ID
 */
export async function createGoal(
  ownerId: string,
  data: Omit<Goal, 'id' | 'createdAt' | 'ownerId'>
): Promise<string> {
  const goalData: Omit<Goal, 'id'> = {
    ...data,
    ownerId,
    createdAt: Date.now(),
    // Fase 1: default status is draft
    status: data.status || 'draft',
  };

  // ⚓ ANCHORED: 2026-01-27 - Verwijder undefined velden voor Firestore.
  const cleanData = removeUndefinedFields(goalData as Record<string, unknown>);

  const docRef = await addDoc(collection(db, COLLECTION_NAME), cleanData);
  
  // Update the document with its own ID for consistency
  await updateDoc(docRef, { id: docRef.id });
  
  return docRef.id;
}

// -----------------------------------------------------------------------------
// CREATE WITH INTAKE (Fase 1)
// -----------------------------------------------------------------------------

/**
 * Create a new goal via the intake flow (Fase 1)
 * This creates a committed goal with all intake data
 * @param ownerId - Firebase Auth UID
 * @param intakeData - Complete intake data from IntakeFlow
 * @returns The created goal's ID
 */
export async function createGoalFromIntake(
  ownerId: string,
  intakeData: {
    title: string;
    description?: string;
    startDate: string;
    endDate: string;
    goalType: GoalType;
    questionnaireAnswers: GoalQuestionnaireAnswers;
    mission: string;
    vision: string;
    commitment: GoalCommitment;
    balanceCategory?: GoalBalanceCategory;
    goalMainCategory?: GoalMainCategory;
  }
): Promise<string> {
  const goalData: Omit<Goal, 'id'> = {
    ownerId,
    title: intakeData.title,
    description: intakeData.description,
    startDate: intakeData.startDate,
    endDate: intakeData.endDate,
    createdAt: Date.now(),
    // Fase 1 specific fields
    status: 'committed',
    goalType: intakeData.goalType,
    questionnaireAnswers: intakeData.questionnaireAnswers,
    mission: intakeData.mission,
    vision: intakeData.vision,
    commitment: intakeData.commitment,
    // Levensbalans categorie voor tijdstracking
    balanceCategory: intakeData.balanceCategory || 'work',
    // Goal main category (personal / business / team)
    goalMainCategory: intakeData.goalMainCategory,
  };

  // ⚓ ANCHORED: 2026-01-27 - Kritieke stap: verwijder undefined velden.
  // Firestore accepteert geen undefined values (bijv. commitment.videoUrl als geen video).
  const cleanData = removeUndefinedFields(goalData as Record<string, unknown>);

  const docRef = await addDoc(collection(db, COLLECTION_NAME), cleanData);
  
  // Update the document with its own ID for consistency
  await updateDoc(docRef, { id: docRef.id });
  
  return docRef.id;
}

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

/**
 * Get a single goal by ID
 * @param goalId - The goal's document ID
 * @returns Goal object or null if not found
 */
export async function getGoalById(goalId: string): Promise<Goal | null> {
  const docRef = doc(db, COLLECTION_NAME, goalId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Goal;
}

/**
 * Get all goals for a user
 * @param ownerId - Firebase Auth UID
 * @returns Array of Goal objects
 */
export async function getGoals(ownerId: string): Promise<Goal[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Goal))
    .sort((a, b) => b.createdAt - a.createdAt);
}

// -----------------------------------------------------------------------------
// UPDATE
// -----------------------------------------------------------------------------

/**
 * Update an existing goal
 * @param goalId - The goal's document ID
 * @param data - Partial goal data to update
 */
export async function updateGoal(
  goalId: string,
  data: Partial<Omit<Goal, 'id' | 'createdAt' | 'ownerId'>>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, goalId);
  // ⚓ ANCHORED: 2026-01-27 - Verwijder undefined velden voor Firestore.
  const cleanData = removeUndefinedFields(data as Record<string, unknown>);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await updateDoc(docRef, cleanData as any);
}

// -----------------------------------------------------------------------------
// DELETE (with cascade)
// -----------------------------------------------------------------------------

/**
 * Delete a goal and all associated milestones and actions
 * @param goalId - The goal's document ID
 */
export async function deleteGoal(goalId: string): Promise<void> {
  // 1. Delete all milestones for this goal
  const milestonesQuery = query(
    collection(db, 'milestones'),
    where('goalId', '==', goalId)
  );
  const milestonesSnapshot = await getDocs(milestonesQuery);
  
  const milestoneDeletePromises = milestonesSnapshot.docs.map(doc => 
    deleteDoc(doc.ref)
  );
  await Promise.all(milestoneDeletePromises);

  // 2. Delete all actions for this goal
  const actionsQuery = query(
    collection(db, 'actions'),
    where('goalId', '==', goalId)
  );
  const actionsSnapshot = await getDocs(actionsQuery);
  
  const actionDeletePromises = actionsSnapshot.docs.map(doc => 
    deleteDoc(doc.ref)
  );
  await Promise.all(actionDeletePromises);

  // 3. Delete the goal itself
  const goalRef = doc(db, COLLECTION_NAME, goalId);
  await deleteDoc(goalRef);
}
